import { FormattedMessage } from 'react-intl';
import { Fragment, ReactNode } from 'react';

import { PaloIncompletePart, PaloMissingField, paloMissingFields } from './palo-missing-fields';

/**
 * The missing fields as one readable line, grouped by the tab they sit on.
 *
 * Built as nodes rather than a string because the labels are translated where
 * the message is shown, not where the save was refused — a snackbar takes its
 * values into the same translation pass as its text.
 */
const joined = (fields: PaloMissingField[]): ReactNode => {
    const tabs = fields.reduce<Record<string, PaloMissingField[]>>((all, field) => {
        all[field.tab] = [...(all[field.tab] ?? []), field];

        return all;
    }, {});

    return Object.entries(tabs).map(([tab, group], index) => (
        <Fragment key={tab}>
            {index > 0 ? ' · ' : null}
            <FormattedMessage id={tab} />
            {': '}
            {group.map((field, position) => (
                <Fragment key={field.field}>
                    {position > 0 ? ', ' : null}
                    <FormattedMessage id={field.labelId} defaultMessage={field.field} />
                </Fragment>
            ))}
        </Fragment>
    ));
};

/**
 * What a mask says when it will not save: the fields it is waiting for, and
 * the tab to find them on — the tab matters because the one holding them need
 * not be the one being looked at (#2212 R-28, #2210 R-98).
 *
 * Returns nothing when there is nothing to name, so a caller can fall back to
 * its general message rather than show an empty list.
 */
export const paloMissingFieldsMessage = (parts: PaloIncompletePart[]): ReactNode | undefined => {
    const fields = paloMissingFields(parts);

    return 0 === fields.length ? undefined : joined(fields);
};
